// Mock for react-native-reanimated on web
// Provides minimal implementation to prevent crashes

import React from 'react';
import { View, Text, Image } from 'react-native';

// Shared values
export const useSharedValue = (initialValue) => ({
    value: initialValue,
});

export const useDerivedValue = (fn) => ({
    value: fn(),
});

export const useAnimatedStyle = (fn) => fn();
export const useAnimatedProps = (fn) => fn();
export const useAnimatedGestureHandler = () => ({});
export const useAnimatedScrollHandler = () => ({});
export const useAnimatedRef = () => React.useRef(null);

// Interpolation
export const interpolate = (value, inputRange, outputRange) => {
    if (typeof value !== 'number') return outputRange[0];
    const inputMin = inputRange[0];
    const inputMax = inputRange[inputRange.length - 1];
    const outputMin = outputRange[0];
    const outputMax = outputRange[outputRange.length - 1];

    if (value <= inputMin) return outputMin;
    if (value >= inputMax) return outputMax;

    const ratio = (value - inputMin) / (inputMax - inputMin);
    return outputMin + ratio * (outputMax - outputMin);
};

export const Extrapolate = {
    EXTEND: 'extend',
    CLAMP: 'clamp',
    IDENTITY: 'identity',
};

export const Extrapolation = Extrapolate;

// Animations
export const withTiming = (toValue, config, callback) => {
    callback && callback(true);
    return toValue;
};

export const withSpring = (toValue, config, callback) => {
    callback && callback(true);
    return toValue;
};

export const withDecay = (config, callback) => {
    callback && callback(true);
    return 0;
};

export const withDelay = (delay, animation) => animation;
export const withSequence = (...animations) => animations[animations.length - 1];
export const withRepeat = (animation, numberOfReps, reverse, callback) => animation;

export const cancelAnimation = () => { };
export const runOnJS = (fn) => fn;
export const runOnUI = (fn) => fn;

// Easing
export const Easing = {
    linear: (t) => t,
    ease: (t) => t,
    quad: (t) => t * t,
    cubic: (t) => t * t * t,
    poly: (n) => (t) => Math.pow(t, n),
    sin: (t) => 1 - Math.cos((t * Math.PI) / 2),
    circle: (t) => 1 - Math.sqrt(1 - t * t),
    exp: (t) => Math.pow(2, 10 * (t - 1)),
    elastic: (bounciness) => (t) => t,
    back: (s) => (t) => t * t * ((s + 1) * t - s),
    bounce: (t) => t,
    bezier: (x1, y1, x2, y2) => (t) => t,
    in: (easing) => easing,
    out: (easing) => (t) => 1 - easing(1 - t),
    inOut: (easing) => (t) => (t < 0.5 ? easing(t * 2) / 2 : 1 - easing((1 - t) * 2) / 2),
};

// Animated components - use regular RN components
export const createAnimatedComponent = (Component) => Component;

// Create Animated namespace that includes animated components
const AnimatedView = View;
const AnimatedText = Text;
const AnimatedImage = Image;
const AnimatedScrollView = View;
const AnimatedFlatList = View;

export const Animated = {
    View: AnimatedView,
    Text: AnimatedText,
    Image: AnimatedImage,
    ScrollView: AnimatedScrollView,
    FlatList: AnimatedFlatList,
    createAnimatedComponent,
};

// Also export View, Text, etc. directly from Animated for default import usage
Animated.default = Animated;

// Layout animations (no-ops)
export const FadeIn = { duration: () => FadeIn };
export const FadeOut = { duration: () => FadeOut };
export const FadeInUp = { duration: () => FadeInUp };
export const FadeOutDown = { duration: () => FadeOutDown };
export const SlideInRight = { duration: () => SlideInRight };
export const SlideOutRight = { duration: () => SlideOutRight };
export const Layout = { duration: () => Layout, springify: () => Layout };
export const ZoomIn = { duration: () => ZoomIn };
export const ZoomOut = { duration: () => ZoomOut };

// Entry/exit animations
export const LinearTransition = { duration: () => LinearTransition };
export const SequencedTransition = { duration: () => SequencedTransition };

// Sensor hooks
export const useAnimatedSensor = () => ({ sensor: { value: {} } });

// Scroll handler
export const useAnimatedReaction = () => { };

// Default export - export Animated directly so `import Animated from 'react-native-reanimated'` works
export default Animated;
